import React, { useState, useEffect, useCallback } from 'react';
import { InputNumber, Select, Button, Alert, Typography } from 'antd';
import Tick from '../../../../../assets/customSVG/Tick';

const { Text } = Typography;

const TIME_UNITS = {
  minutes: {
    label: 'Minute(s)',
    multiplier: 60,
    min: 2,
    max: 59,
    singular: 'minute',
    plural: 'minutes',
  },
  hours: {
    label: 'Hour(s)',
    multiplier: 3600,
    min: 1,
    max: 23,
    singular: 'hour',
    plural: 'hours',
  },
  days: {
    label: 'Day(s)',
    multiplier: 86400,
    min: 1,
    max: 7,
    singular: 'day',
    plural: 'days',
  },
};

const FollowUpPreview = ({
  node,
  onUpdateWaitTime,
  isUpdating = false,
  handleOk,
  setRemovedEdges,
  allowToEditFlow,
}) => {
  const [timeValue, setTimeValue] = useState(1);
  const [timeUnit, setTimeUnit] = useState('hours');
  const [error, setError] = useState('');

  // Convert seconds to appropriate time value and unit (same logic as working component)
  const convertSecondsToTimeValue = (totalSeconds) => {
    if (!totalSeconds) return { value: 1, unit: 'hours' };

    // Try to find the best unit that gives a whole number
    const units = ['days', 'hours', 'minutes', 'seconds'];

    for (const unit of units) {
      const { multiplier, min, max } = TIME_UNITS[unit];
      const value = totalSeconds / multiplier;

      if (value >= min && value <= max && Number.isInteger(value)) {
        return { value, unit };
      }
    }

    // Fallback to minutes if no perfect match
    const minuteValue = Math.round(totalSeconds / 60);
    return {
      value: Math.max(1, Math.min(59, minuteValue)),
      unit: 'minutes',
    };
  };

  // Initialize with data from props and handle node changes
  useEffect(() => {
    const delay = node?.data?.delay;
    if (delay !== undefined) {
      const { value, unit } = convertSecondsToTimeValue(delay);
      // Only update if the values have actually changed to prevent infinite loops
      setTimeValue((prev) => (prev !== value ? value : prev));
      setTimeUnit((prev) => (prev !== unit ? unit : prev));
    } else if (node?.data) {
      // If node exists but no delay is set, initialize with defaults
      setTimeValue(1);
      setTimeUnit('hours');
    }
  }, [node?.data?.delay, node?.uniq_id]); // Add uniq_id to track node changes

  // Validation logic (same as working component)
  const validateTime = (value, unit) => {
    const { min, max, multiplier } = TIME_UNITS[unit];
    const totalSeconds = value * multiplier;

    // Check unit-specific limits
    if (value < min || value > max) {
      return `Value must be between ${min} and ${max} ${unit}`;
    }

    // Check absolute limits (30 seconds to 7 days)
    const MIN_SECONDS = 30;
    const MAX_SECONDS = 7 * 24 * 60 * 60; // 7 days

    if (totalSeconds < MIN_SECONDS) {
      return 'Minimum wait time is 30 seconds';
    }

    if (totalSeconds > MAX_SECONDS) {
      return 'Maximum wait time is 7 days';
    }

    return '';
  };

  // Display current wait time (same logic as working component)
  const getCurrentWaitTimeDisplay = () => {
    const totalSeconds = timeValue * TIME_UNITS[timeUnit].multiplier;
    if (totalSeconds === 0) return '';

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);

    return parts.length > 0 ? `${parts.join(' ')}` : '';
  };

  // Centralized time update handler
  const updateTime = useCallback(
    (newValue, newUnit) => {
      const numValue = parseInt(newValue) || 1;
      const unit = newUnit || timeUnit;

      // Update local state
      setTimeValue(numValue);
      if (newUnit) setTimeUnit(newUnit);

      // Validate
      const validationError = validateTime(numValue, unit);
      if (validationError) {
        setError(validationError);
        return;
      }

      // If valid, update local state
      setError('');
      const totalSeconds = numValue * TIME_UNITS[unit].multiplier;

      // Update parent component
      onUpdateWaitTime?.(totalSeconds);
    },
    [onUpdateWaitTime, timeUnit]
  );

  const handleAmountChange = (value) => {
    updateTime(value, null);
  };

  const handleUnitChange = (selectedUnit) => {
    updateTime(timeValue, selectedUnit);
  };

  const handleOnSubmit = () => {
    const delayInSeconds = timeValue * TIME_UNITS[timeUnit].multiplier;
    const currentDelay = node?.data?.operations?.delay || 0;
    let edgesToRemove = [];

    // Check if we're crossing the 24-hour threshold
    const isCurrentUnder24h = currentDelay < 24 * 60 * 60; // 24 hours in seconds
    const isNewUnder24h = delayInSeconds < 24 * 60 * 60;

    // If we're crossing the 24h threshold, find the edge to remove
    if (
      (isCurrentUnder24h && !isNewUnder24h) ||
      (!isCurrentUnder24h && isNewUnder24h)
    ) {
      const nextStepEdge = node?.settings?.connections?.points?.find(
        (point) => point.type === 'next_steps'
      );
      if (nextStepEdge?.edge?.slug) {
        edgesToRemove = [nextStepEdge.edge.slug];
      }
    }

    // Add 's' if value is not 1, otherwise keep it singular
    const unitLabel =
      timeValue === 1
        ? TIME_UNITS[timeUnit].singular
        : TIME_UNITS[timeUnit].plural;
    const label = `Wait for ${timeValue} ${unitLabel}`;

    const data = {
      ...node?.data,
      type: 'follow_up',
      label,
      follow_up: {
        ...(node?.data?.follow_up || {}),
        delay: delayInSeconds,
      },
      operations: {
        ...(node?.data?.operations || {}),
        delay: delayInSeconds,
      },
    };

    // Pass the edges to remove back to the parent
    handleOk(data, label, []);
  };

  const getTimeUnitOptions = () => {
    return Object.entries(TIME_UNITS).map(([key, { label, min, max }]) => (
      <Select.Option key={key} value={key}>
        {label}
      </Select.Option>
    ));
  };

  const connection_point = { id: node?.data?.id, type: 'action' };
  const currentUnit = TIME_UNITS[timeUnit];

  return (
    <div className="relative w-full h-full">
      <div className="relative w-full flex flex-col">
        <div className="w-full h-full bg-white flex flex-col p-[12px] shadow-[0px_4px_30px_0px_#2D30361A]">
          <p className="text-[0.813rem]">
            Set the waiting time before sending the follow-up message
          </p>
          <p className="text-[0.813rem] mb-4 mt-1"><span className="underline font-medium">Note:</span> <span className="italic text-gray-500"> The follow ups message will be  sent only to the  previously delivered contacts.</span></p>

          <div className="text-[0.813rem] mb-2 font-medium">Set delay</div>

          <div className="flex items-center gap-3 mb-4">
            <div className="text-[0.813rem] w-16 shrink-0">Wait for</div>

            <InputNumber
              min={currentUnit?.min || 0}
              max={currentUnit?.max || 999}
              value={timeValue}
              onChange={handleAmountChange}
              className="!w-28"
              disabled={isUpdating || !allowToEditFlow}
              type="number"
              pattern="[0-9]*"
            />

            <Select
              value={timeUnit}
              onChange={handleUnitChange}
              className="!w-36"
              disabled={isUpdating || !allowToEditFlow}
            >
              {getTimeUnitOptions()}
            </Select>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              className="mb-4"
              size="small"
            />
          )}

          {/* <div className="text-xs text-gray-500 mb-4">
            <div>Result: {getCurrentWaitTimeDisplay()}</div>
            <div className="mt-1 text-gray-400">
              Range: 30 seconds - 7 days
            </div>
          </div> */}

          <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
            <Text type="secondary" className="text-xs block">
              <strong>Usage Examples:</strong>
              <br />• 2 minutes – 59 minutes: For short delays
              <br />• 1 hour – 23 hours: For same-day follow-ups
              <br />• 1 day – 7 days: For long-term sequences
            </Text>
          </div>

          {allowToEditFlow && (
            <div className="flex justify-end border-t border-gray-200 pt-2">
              <Button
                type="primary"
                icon={
                  <Tick
                    width="12"
                    height="10"
                    strokeWidth="2.5"
                    stroke="white"
                  />
                }
                onClick={handleOnSubmit}
                className="!bg-[#05805c] hover:!bg-[#05805c] px-2 !text-[0.813rem]"
                disabled={!!error || isUpdating}
                loading={isUpdating}
              >
                OK
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowUpPreview;
